/**
 * ShadowDiscoveryPageV2 — proper v2 redesign of Agent Discovery (G109).
 *
 * Canonical Agentlets v2 inventory pattern:
 *   FacetRail (Status / Risk tier / Source engine / Confidence band)
 *     + v2 Table (urgency dot · agent · type · confidence · source · last seen · actions)
 *     + Slideout (5 tabs: Summary · Detection signals · Communication · Investigate · Promote)
 *     + Bulk action toolbar (investigate / enroll / dismiss)
 *
 * Reuses every backend code path from v1 ShadowDiscoveryPage:
 *   - api.shadowDiscoveryDashboard / Agents / Graph / Scans / Rules / Signals
 *   - api.shadowDiscoveryUpdateStatus / Enroll / Dismiss / BulkStatus / ResolveSignal
 *
 * v1 stays as fallback via /shadow-discovery?legacy=1.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { isRebirthDiscoveryEnabled } from '../utils/featureFlags';
import * as api from '../services/api';
import { useDiscoveryScanContext } from './discovery/DiscoveryScanContext';
/* Phase 2 picker — Run-scan goes through the configurable trigger
 * modal so the operator confirms engines/rules/targets before any
 * workflow fires. Previously, the Agents-tab header + empty-state
 * "Run scan" buttons bypassed the modal and called scanCtx.triggerScan()
 * directly — a real regression: the operator had no chance to pick
 * engines/rules/targets, no Pause/Stop opportunity, scan just ran.
 * Funnelling every entry point through the modal restores the
 * "no scan runs until START" contract. */
import ScanTriggerModal from './discovery/ScanTriggerModal';
import {
  RefreshCw, Bot, Eye, Activity, AlertTriangle, CheckCircle, X, Search,
  Network, ChevronRight, MoreVertical, CalendarClock,
} from 'lucide-react';
import { TrustScorePanel } from '../components/v2/trust/TrustScorePanel';
import {
  Button, Pill, Card, Modal, Slideout, Table, FacetRail, EmptyState,
  MetricCard, TabNav,
  /* EXTELET-V3 · Phase 6 — replaces the old V1 enroll path on single-row Promote. */
  ExteletRegistrationWizard,
} from '../components/v2';
import { Pagination, usePaginationState } from '../components/common/Pagination';

import ShadowDiscoveryPage from './ShadowDiscoveryPage';

const STATUS_OPTIONS = [
  { key: 'NEW',           label: 'New',            tone: 'cyan' },
  { key: 'INVESTIGATING', label: 'Investigating',  tone: 'warning' },
  { key: 'CONFIRMED',     label: 'Confirmed',      tone: 'danger' },
  { key: 'ENROLLED',      label: 'Enrolled',       tone: 'success' },
  { key: 'DISMISSED',     label: 'Dismissed',      tone: 'muted' },
];

/* States an operator has already decided on. Nothing here is awaiting triage, so
 * the default table leaves them out and the detail panel offers a way back rather
 * than offering the decision again. */
const TERMINAL_STATUSES = new Set(['ENROLLED', 'DISMISSED']);

/* The one place that answers "what can an operator do to a row in this state?".
 *
 * Every action surface on this page — the inline row buttons, the overflow menu,
 * the detail-panel action row, and the Investigate / Promote tab bodies — asks
 * this function instead of testing `status` itself. Before this existed each
 * surface carried its own hand-written status test, written at a different time
 * for a different change, and nothing kept them in agreement: a dismissed agent
 * still offered "Mark as Investigating", "Enroll as ExteLet" and a second
 * "Dismiss…". Adding a surface now means reading a flag, not re-deriving the
 * rule and getting it subtly wrong.
 *
 * `reason` is the operator-facing explanation for why a decided row offers no
 * further decision. It is rendered verbatim, so it says what to do next. */
const agentActions = (status) => {
  const terminal = TERMINAL_STATUSES.has(status);
  return {
    canInvestigate: status === 'NEW' || status === 'INVESTIGATING',
    canPromote: status === 'NEW' || status === 'INVESTIGATING' || status === 'CONFIRMED',
    canDismiss: !terminal,
    canRestore: status === 'DISMISSED',
    isTerminal: terminal,
    reason:
      status === 'DISMISSED'
        ? 'This agent was dismissed. Restore it to triage to investigate or promote it.'
        : status === 'ENROLLED'
          ? 'This agent is already governed — it has an ExteLet with policy bindings, trust scoring, and an audit trail.'
          : null,
  };
};

/* Phase A · Agent Discovery — Seven-engine taxonomy (see docs/AGENT_DISCOVERY_DESIGN.md §18).
 * Order is the same one the engine status strip renders left → right.
 * MARKETPLACE is reserved for Phase D; the BE returns enabled=false with
 * reason="phase_d" for that slot so the FE can grey-out the dot. */
const ENGINE_OPTIONS = [
  { key: 'NETWORK',      label: 'Network'     },
  { key: 'MCP_PROTOCOL', label: 'Protocol'    },
  { key: 'CODE_REPO',    label: 'Code'        },
  { key: 'CLOUD_SCAN',   label: 'Cloud'       },
  { key: 'IDENTITY',     label: 'Identity'    },
  { key: 'BEHAVIORAL',   label: 'Behavior'    },
  { key: 'GATEWAY',      label: 'Gateway'     },
  { key: 'MARKETPLACE',  label: 'Marketplace' },
];
const RISK_OPTIONS = [
  { key: 'critical', label: 'Critical', tone: 'danger' },
  { key: 'high',     label: 'High',     tone: 'warning' },
  { key: 'medium',   label: 'Medium',   tone: 'cyan' },
  { key: 'low',      label: 'Low',      tone: 'muted' },
];
const STATUS_TONE = Object.fromEntries(STATUS_OPTIONS.map(o => [o.key, o.tone]));
const RISK_TONE = Object.fromEntries(RISK_OPTIONS.map(o => [o.key, o.tone]));

function fmtRelative(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

function ShadowDiscoveryPageV2Inner() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  /* Phase H — when this page is rendered under DiscoveryHubPage the
   * provider supplies an async scan trigger; the drawer mounted at the
   * hub level then shows live progress. When rendered standalone
   * (legacy /discovery-classic path) the context is null and we fall
   * back to the old sync trigger so nothing breaks. */
  const scanCtx = useDiscoveryScanContext();

  const [dashboard, setDashboard] = useState(null);
  const [agents, setAgents] = useState([]);
  const [signals, setSignals] = useState([]);
  /* Phase A · 7-engine status strip data */
  const [engineStatus, setEngineStatus] = useState(null);
  /* Bug #86 Phase 1 — per-engine rule readiness drives the amber gating
   * banner, the disabled-Run-scan button + tooltip, and the V2
   * rule-status slideout. Shape mirrors the BE: { totalEnabledRules,
   * totalSupportedRules, engines: [{engineType, label, supportedRules,
   * enabledCount, totalCount}, ...] }. `null` = not loaded yet, object
   * = loaded. */
  const [ruleStatus, setRuleStatus] = useState(null);
  const [ruleSlideoutOpen, setRuleSlideoutOpen] = useState(false);
  /* Phase 4 (task #95) — starter pack install in-flight flag. Disables
   * the StarterPackInstallTile button so a double-click can't fire two
   * concurrent installs. The BE service is idempotent anyway but
   * blocking the button is the right UX. */
  const [starterInstalling, setStarterInstalling] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [dash, ag, sigs, engStat, ruleStat] = await Promise.all([
        api.shadowDiscoveryDashboard?.()?.catch(() => null),
        /* DEF-DISC-COUNT-MISMATCH-001 — BE defaults pagination to size=20.
         * The Discovery → Agents tab is the staging area, not an explorer:
         * the operator needs to see EVERY discovered agent to triage them.
         * Page-size 1000 covers the realistic upper bound (one scan run finds
         * ~600 langchain hits across two orgs); we paginate the FE table
         * client-side via the existing usePaginationState(10) at line 203. */
        api.shadowDiscoveryAgents?.({ size: 10000 })?.catch(() => []),
        api.shadowDiscoverySignals?.()?.catch(() => []),
        api.shadowDiscoveryEngineStatus?.()?.catch(() => null),
        /* Bug #86 Phase 1 — per-engine rule readiness for banner +
         * slideout + button gate. Fail-soft to null so the page still
         * renders if the endpoint is unavailable on older builds. */
        api.shadowDiscoveryEngineRuleStatus?.()?.catch(() => null),
      ]);
      setDashboard(dash || null);
      setEngineStatus(engStat?.data ?? engStat ?? null);
      setRuleStatus(ruleStat?.data ?? ruleStat ?? null);
      // G153 — defensive shape unwrapping. The backend may return:
      //   bare array  → use as-is
      //   { ok, data } from apiFetch → unwrap .data, which itself may be:
      //     - an array
      //     - { content: [], totalElements: N }  (Spring Page)
      //     - { items: [] }
      //     - { agents: [], total: N }   (ShadowDiscoveryController paged response)
      // Anything we can't recognize falls back to [] so the downstream
      // `for (const a of agents)` never throws "not iterable".
      const unwrapList = (raw) => {
        if (Array.isArray(raw)) return raw;
        const data = raw?.data ?? raw;
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.content)) return data.content;
        if (Array.isArray(data?.items)) return data.items;
        /* DEF-DISC-COUNT-MISMATCH-001 — recognise the {agents, total}
         * shape ShadowDiscoveryController returns. Previously this fell
         * through to the [] fallback, which is why the FE showed "0
         * discovered" or stale 20 rows from another shape. */
        if (Array.isArray(data?.agents)) return data.agents;
        return [];
      };
      /* The list endpoint returns the raw DiscoveredAgent record, whose fields
       * are discoveryConfidence / firstSeenAt / lastSeenAt. The table + slideout
       * read confidence / firstSeen / lastSeen, so normalise the aliases once
       * here (for every engine) rather than at each read site. */
      setAgents(unwrapList(ag).map((a) => (a && typeof a === 'object' ? {
        ...a,
        confidence: a.confidence ?? a.discoveryConfidence,
        firstSeen: a.firstSeen ?? a.firstSeenAt,
        lastSeen: a.lastSeen ?? a.lastSeenAt,
        type: a.type ?? a.agentType,
      } : a)));
      setSignals(unwrapList(sigs));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  /* ── Facets ──────────────────────────────────────────────── */
  const [facets, setFacets] = useState(() => ({
    status:     new Set(),
    riskTier:   new Set(),
    sourceEngine: new Set(),
    confidence: new Set(),
  }));
  const toggleFacet = (groupKey, optionKey, nextChecked) => {
    setFacets(prev => {
      const next = new Set(prev[groupKey] || []);
      if (nextChecked) next.add(optionKey); else next.delete(optionKey);
      return { ...prev, [groupKey]: next };
    });
  };
  const clearFacets = () => setFacets({
    status: new Set(), riskTier: new Set(), sourceEngine: new Set(), confidence: new Set(),
  });

  /* Derive sourceEngine option list dynamically */
  const sourceEngines = useMemo(() => {
    const set = new Set();
    for (const a of agents) {
      const eng = a.sourceEngine || a.source || a.engine;
      if (eng) set.add(eng);
    }
    return Array.from(set).sort();
  }, [agents]);

  const FACET_GROUPS = useMemo(() => ([
    { key: 'status',       label: 'Status',
      options: STATUS_OPTIONS.map(s => ({ key: s.key, label: s.label })) },
    { key: 'riskTier',     label: 'Risk tier',
      options: RISK_OPTIONS.map(r => ({ key: r.key, label: r.label })) },
    { key: 'sourceEngine', label: 'Source engine',
      options: sourceEngines.map(e => ({ key: e, label: e })) },
    { key: 'confidence',   label: 'Confidence',
      options: [
        { key: 'high', label: '≥ 80%' },
        { key: 'mid',  label: '50-80%' },
        { key: 'low',  label: '< 50%' },
      ]},
  ]), [sourceEngines]);

  const confidenceBand = (c) => {
    const n = typeof c === 'number' ? c : (parseFloat(c) || 0);
    if (n >= 0.8 || n >= 80) return 'high';
    if (n >= 0.5 || n >= 50) return 'mid';
    return 'low';
  };

  const filtered = useMemo(() => {
    return agents.filter(a => {
      /* Phase A · sign-off #6 — hide ENROLLED by default. The KPI tile
       * for Enrolled still shows the real count; the table focuses on
       * actionable rows (NEW / INVESTIGATING / CONFIRMED). Operator can
       * explicitly tick the Enrolled chip in the Status facet to surface
       * them; once any Status filter is set, the user is driving and we
       * respect their choice verbatim. */
      /* Terminal states are hidden until the operator asks for them. ENROLLED was
       * the only one listed here, so a row the operator had explicitly dismissed
       * stayed in a table whose own contract is "actionable rows" — and a re-scan
       * bumping lastSeenAt floated it back to the top reading "just now", which
       * looks like the dismissal was undone. It was not: the status persists. */
      if (facets.status.size === 0 && TERMINAL_STATUSES.has(a.status)) return false;
      if (facets.status.size > 0 && !facets.status.has(a.status)) return false;
      if (facets.riskTier.size > 0 && !facets.riskTier.has((a.riskTier || a.risk || '').toLowerCase())) return false;
      const eng = a.sourceEngine || a.source || a.engine;
      if (facets.sourceEngine.size > 0 && !facets.sourceEngine.has(eng)) return false;
      if (facets.confidence.size > 0 && !facets.confidence.has(confidenceBand(a.confidence))) return false;
      return true;
    });
  }, [agents, facets]);

  /* G175 — Pagination over the filtered list. Reset to page 1 whenever the
   * filtered length changes so the user doesn't land on an empty page. */
  const pg = usePaginationState(10);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); }, [filtered.length]);

  /* Counts */
  const facetCounts = useMemo(() => {
    const counts = {};
    for (const g of FACET_GROUPS) {
      counts[g.key] = {};
      for (const opt of g.options) {
        counts[g.key][opt.key] = agents.filter(a => {
          for (const og of FACET_GROUPS) {
            if (og.key === g.key) continue;
            const sel = facets[og.key];
            if (!sel || sel.size === 0) continue;
            if (og.key === 'sourceEngine') {
              const eng = a.sourceEngine || a.source || a.engine;
              if (!sel.has(eng)) return false;
            } else if (og.key === 'confidence') {
              if (!sel.has(confidenceBand(a.confidence))) return false;
            } else if (og.key === 'riskTier') {
              if (!sel.has((a.riskTier || a.risk || '').toLowerCase())) return false;
            } else {
              if (!sel.has(a[og.key])) return false;
            }
          }
          if (g.key === 'sourceEngine') return (a.sourceEngine || a.source || a.engine) === opt.key;
          if (g.key === 'confidence')   return confidenceBand(a.confidence) === opt.key;
          if (g.key === 'riskTier')     return (a.riskTier || a.risk || '').toLowerCase() === opt.key;
          return a[g.key] === opt.key;
        }).length;
      }
    }
    return counts;
  }, [agents, facets, FACET_GROUPS]);

  const facetGroupsWithCounts = useMemo(() => (
    FACET_GROUPS.map(g => ({
      ...g,
      options: g.options.map(o => ({
        ...o,
        count: facetCounts[g.key]?.[o.key] || 0,
        checked: facets[g.key]?.has(o.key) || false,
      })),
    }))
  ), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) {
      for (const optKey of facets[g.key] || []) {
        const opt = g.options.find(o => o.key === optKey);
        chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
      }
    }
    return chips;
  }, [facets, FACET_GROUPS]);

  /* Density */
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem('shadow-density') || 'comfortable'; }
    catch { return 'comfortable'; }
  });
  const cycleDensity = () => {
    const next = density === 'comfortable' ? 'compact' : 'comfortable';
    setDensity(next);
    try { localStorage.setItem('shadow-density', next); } catch {}
  };

  /* Slideout */
  const [selected, setSelected] = useState(null);
  /* Capabilities for the row in the detail panel. Null-safe: before a row is
   * selected every flag is false, which is what the panel's own `selected &&`
   * guards already assume. */
  const selectedCan = React.useMemo(() => agentActions(selected?.status), [selected?.status]);
  const [slideoutTab, setSlideoutTab] = useState('summary');
  const openSlideout = (row) => { setSelected(row); setSlideoutTab('summary'); };
  const closeSlideout = () => setSelected(null);

  /* DEF-DISC-SHADOW-FILTER-DEADEND-001 — honor ?selected={id} deep-link
   * coming in from /agents shadow-filter clicks (AgentsInventoryPageV2
   * openSlideout). When agents finish loading and the URL carries a
   * selected param matching one of them, auto-open its slideout for
   * triage. One-shot: clears the search param after consuming so a
   * subsequent navigation doesn't re-open a stale slideout. */
  const selectedLandedRef = React.useRef(false);
  useEffect(() => {
    if (selectedLandedRef.current) return;
    if (!agents || agents.length === 0) return;
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const wantId = url.searchParams.get('selected');
    if (!wantId) return;
    const match = agents.find(a => a.id === wantId);
    if (!match) return;
    selectedLandedRef.current = true;
    setSelected(match);
    setSlideoutTab('summary');
    /* Clean the param so a Refresh or a subsequent navigation doesn't
     * re-trigger the auto-open. */
    url.searchParams.delete('selected');
    window.history.replaceState({}, '', url.pathname + (url.search ? url.search : '') + url.hash);
  }, [agents]);

  /* Bulk selection */
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelected = () => setSelectedIds(new Set());

  /* Phase A · sign-off #2 — per-row Dismiss moves to an overflow menu so
   * accidental clicks need an extra deliberate gesture. State tracks which
   * row has its overflow open (single-open at a time). */
  const [overflowOpenId, setOverflowOpenId] = useState(null);
  const toggleOverflow = (id) => setOverflowOpenId(prev => prev === id ? null : id);

  /* Actions — same backend code paths */
  const handleInvestigate = async (id) => {
    try {
      await api.shadowDiscoveryUpdateStatus?.(id, { status: 'INVESTIGATING' });
      addToast({ tone: 'info', title: 'Marked for investigation' });
      reload();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Investigate failed', description: err.message });
    }
  };
  /* EXTELET-V3 · Phase 6 — single-row Promote opens the new 6-step
   * wizard (atomic register + Gateway/SDK/MCP key mint + snippet).
   * The legacy handleEnroll path below is preserved untouched as the
   * fallback for any caller that still depends on the V1 shape — and
   * for bulk Promote (handleBulkPromote above), which intentionally
   * stays on the V1 endpoint because bulk-creating credentials with
   * one-time plaintext display doesn't fit the wizard's per-agent
   * forcing-function model. */
  const [wizardSeed, setWizardSeed] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  const openPromoteWizard = (row) => {
    /* Seed the wizard with everything Discovery already knows so the
     * operator doesn't re-type. The wizard projects this onto its
     * step 1 + step 2 + step 3 (auto-recommends path from
     * discoverySource). */
    setWizardSeed({
      id: row.id,
      name: row.name || row.externalAgentName || row.agentName,
      externalAgentName: row.externalAgentName || row.name,
      externalAgentId: row.externalAgentId || row.id,
      version: row.version || row.externalAgentVersion,
      platformConnectorId: row.platformConnectorId || row.suggestedConnectorId,
      platformType: row.platformType || row.framework,
      discoverySource: row.discoverySource || row.source,
    });
    setWizardOpen(true);
  };

  const closePromoteWizard = () => {
    setWizardOpen(false);
    /* Leave wizardSeed populated for a beat so the close animation
     * doesn't flash blank fields. The next openPromoteWizard call
     * replaces it. */
  };

  const handleEnroll = async (id) => {
    try {
      // DEF-DISCOVERY-FE-ENROLL-TOAST-001 — apiFetch never throws on a
      // non-2xx; it returns { ok: false, error } on failure. Without
      // this guard the FE fired the success toast even when the BE
      // rejected the enrollment with 400/409/500 and the agent stayed
      // in NEW status — operator thought the promote worked when it
      // actually did nothing.
      const raw = await api.shadowDiscoveryEnroll?.(id, {});
      if (raw && raw.ok === false) {
        const msg = raw.error || raw.message || 'Enrollment rejected';
        addToast({ tone: 'danger', title: 'Enroll failed', description: msg });
        return;
      }
      // DEF-DISCOVERY-PROMOTE-NAV-001 — auto-navigate to the new ExteLet's
      // slideout on /gel-agents (the operator wants to land directly in
      // the BUILD-phase governance surface for the agent they just
      // promoted, not have to find it manually). The BE response
      // includes the new registrationId; we pass it as ?selected=…
      // and the GelExternalAgentsPageV2 page auto-opens that row's
      // slideout on mount.
      const payload = raw?.data || raw || {};
      const registrationId = payload.registrationId;
      const agentId = payload.agentId;
      addToast({ tone: 'success', title: 'Agent promoted to ExteLet',
        description: 'Opening BUILD-phase protection surface…' });
      closeSlideout();
      if (registrationId) {
        navigate(`/gel-agents?selected=${registrationId}`);
      } else if (agentId) {
        navigate(`/gel-agents?agentId=${agentId}`);
      } else {
        // Fallback — BE didn't return ids (shouldn't happen with our
        // current backend, but be defensive).
        navigate('/gel-agents');
      }
    } catch (err) {
      addToast({ tone: 'danger', title: 'Enroll failed', description: err?.message || String(err) });
    }
  };
  const handleDismiss = async (id) => {
    try {
      await api.shadowDiscoveryDismiss?.(id, { reason: 'user_dismissed' });
      addToast({ tone: 'info', title: 'Agent dismissed' });
      closeSlideout();
      reload();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Dismiss failed', description: err.message });
    }
  };
  /* The way back from a dismissal. Without it the only correction available was
   * Promote, which enrolls — a much larger step than "I dismissed that by mistake". */
  const handleRestore = async (id) => {
    try {
      await api.shadowDiscoveryUpdateStatus?.(id, { status: 'NEW' });
      addToast({ tone: 'info', title: 'Restored to triage' });
      closeSlideout();
      reload();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Restore failed', description: err.message });
    }
  };
  const handleBulk = async (status) => {
    try {
      await api.shadowDiscoveryBulkStatus?.({ agentIds: Array.from(selectedIds), status });
      addToast({ tone: 'success', title: `Bulk ${status.toLowerCase()} complete` });
      clearSelected();
      reload();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Bulk action failed', description: err.message });
    }
  };
  /* Phase A · Bulk Promote (real enrollment).
   * The old bulk Enroll button used `handleBulk('ENROLLED')` which only
   * flips status=ENROLLED on the DiscoveredAgent row WITHOUT creating a
   * governed Agent. The new endpoint loops per-row enrollAgent. Partial
   * failures surface as a warning toast with the counts. */
  const handleBulkPromote = async () => {
    try {
      const res = await api.shadowDiscoveryBulkPromote?.({
        agentIds: Array.from(selectedIds),
        enrollmentMethod: 'BULK_PROMOTE',
      });
      const data = res?.data ?? res ?? {};
      const promoted = data.promoted ?? 0;
      const failed = data.failed ?? 0;
      if (failed > 0) {
        addToast({
          tone: 'warning',
          title: `${promoted} promoted · ${failed} failed`,
          description: 'Per-row failures are recorded in the audit trail.',
        });
      } else {
        addToast({ tone: 'success', title: `${promoted} agent(s) promoted` });
      }
      clearSelected();
      reload();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Bulk promote failed', description: err?.message || String(err) });
    }
  };
  /* Phase A · Bulk Dismiss with shared reason.
   * Reason captured once via a prompt; applied verbatim to every
   * dismissed row's audit trail. */
  const [bulkDismissPromptOpen, setBulkDismissPromptOpen] = useState(false);
  const [bulkDismissReason, setBulkDismissReason] = useState('');
  const openBulkDismissPrompt = () => {
    setBulkDismissReason('');
    setBulkDismissPromptOpen(true);
  };
  const handleBulkDismissConfirm = async () => {
    const reason = bulkDismissReason.trim();
    if (!reason) {
      addToast({ tone: 'warning', title: 'Reason is required' });
      return;
    }
    try {
      const res = await api.shadowDiscoveryBulkDismiss?.({
        agentIds: Array.from(selectedIds),
        reason,
      });
      const data = res?.data ?? res ?? {};
      const dismissed = data.dismissed ?? 0;
      const failed = data.failed ?? 0;
      if (failed > 0) {
        addToast({
          tone: 'warning',
          title: `${dismissed} dismissed · ${failed} failed`,
        });
      } else {
        addToast({ tone: 'info', title: `${dismissed} agent(s) dismissed`, description: `Reason: ${reason}` });
      }
      setBulkDismissPromptOpen(false);
      clearSelected();
      reload();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Bulk dismiss failed', description: err?.message || String(err) });
    }
  };
  /* Phase 2 (UX regression fix) — picker state. Every Run-scan
   * entry point on this page (Agents-tab header button + empty-state
   * CTA) now opens the picker first. The scan never fires until the
   * operator clicks "Run N engines · rules" inside the modal. */
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleScan = () => {
    /* Bug #86 Phase 1 — local short-circuit gate. Every Run-scan entry
     * funnels through here: if no rules are enabled, route to the
     * rule-status slideout (operator should install the starter pack
     * or author rules before any scan can possibly find anything).
     * NEVER fire a scan from this handler directly — open the picker. */
    if (ruleStatus != null && (ruleStatus.totalEnabledRules ?? 0) === 0) {
      setRuleSlideoutOpen(true);
      return;
    }
    /* Picker is the only path to triggerScan. The picker calls
     * handlePickerConfirm below with the operator's enginePicks
     * payload. */
    setPickerOpen(true);
  };

  /* Phase 2 picker → triggerScan adapter. Normalises the picker's
   * {enginePicks:[…]} payload into the DiscoveryScanContext.triggerScan
   * kwargs shape. Mirrors the DiscoveryHubPage.ScansTabActions
   * confirm handler so behaviour is identical regardless of which
   * Run-scan entry point the operator used. */
  const handlePickerConfirm = async (payload) => {
    if (!scanCtx || typeof scanCtx.triggerScan !== 'function') {
      /* Legacy standalone V2 outside the hub provider — fall back to
       * the sync trigger. Same fallback as the pre-fix handleScan. */
      try {
        await api.shadowDiscoveryScanTrigger?.({ engineTypes: [] });
        setPickerOpen(false);
        addToast({ tone: 'info', title: 'Discovery scan triggered', description: 'Results will appear as engines report in.' });
      } catch (err) {
        addToast({ tone: 'danger', title: 'Scan failed', description: err?.message || String(err) });
      }
      return;
    }
    let triggerArgs;
    let summaryLabel;
    if (payload && Array.isArray(payload.enginePicks)) {
      triggerArgs = { enginePicks: payload.enginePicks };
      const engineCount = payload.enginePicks.length;
      const ruleCount = payload.enginePicks.reduce((sum, p) => sum + ((p.ruleIds || []).length), 0);
      const tgtCount = payload.enginePicks.reduce((sum, p) => sum + ((p.targets || []).length), 0);
      const ruleSeg = ruleCount > 0 ? `${ruleCount} rule${ruleCount === 1 ? '' : 's'}` : 'all enabled rules';
      const tgtSeg = tgtCount > 0 ? ` with ${tgtCount} target override${tgtCount === 1 ? '' : 's'}` : '';
      summaryLabel = `${engineCount} engine${engineCount === 1 ? '' : 's'} · ${ruleSeg}${tgtSeg}`;
    } else if (Array.isArray(payload)) {
      triggerArgs = { engineTypes: payload };
      summaryLabel = payload.length > 0
        ? `${payload.length} engine${payload.length === 1 ? '' : 's'}`
        : 'all engines';
    } else {
      triggerArgs = {};
      summaryLabel = 'all engines';
    }
    try {
      await scanCtx.triggerScan(triggerArgs);
      setPickerOpen(false);
      addToast({
        tone: 'info',
        title: 'Discovery scan started',
        description: `Running ${summaryLabel}. See the progress drawer for live engine status.`,
      });
    } catch (err) {
      /* Bug #86 Phase 1 — race-condition safety. If the rule was
       * disabled in the window between the rule-status fetch and the
       * POST, the backend returns 422 NO_RULES_CONFIGURED. Re-route
       * to the rule-status slideout instead of a generic "Scan failed". */
      const isNoRules =
        err?.status === 422 ||
        err?.data?.code === 'NO_RULES_CONFIGURED' ||
        err?.response?.data?.code === 'NO_RULES_CONFIGURED' ||
        (typeof err?.message === 'string' && /NO_RULES_CONFIGURED|422/.test(err.message));
      if (isNoRules) {
        reload?.();
        setPickerOpen(false);
        setRuleSlideoutOpen(true);
        return;
      }
      addToast({ tone: 'danger', title: 'Scan failed', description: err?.message || String(err) });
    }
  };

  /* KPIs */
  const kpis = useMemo(() => {
    const total = agents.length;
    const newCount = agents.filter(a => a.status === 'NEW').length;
    const investigating = agents.filter(a => a.status === 'INVESTIGATING').length;
    const confirmed = agents.filter(a => a.status === 'CONFIRMED').length;
    const enrolled = agents.filter(a => a.status === 'ENROLLED').length;
    return { total, newCount, investigating, confirmed, enrolled };
  }, [agents]);

  /* DEF-DISC-MARGIN-001 (revised) — Page-shell width is 1600px, not
   * 1400px. The canonical reference is WorkflowDiscoveryPageV2.jsx
   * (/workflow-discovery), which uses max-w-[1600px] — that's what the
   * user pointed to as the correct visual baseline. Discovery hub +
   * this page now use the same 1600px container so the left/right
   * gutters from the sidebar match Discover Workflows exactly.
   *
   * History: first revision changed 1500 → 1400 thinking 1400 was the
   * canon. User flagged the visual mismatch when comparing to
   * /workflow-discovery (which actually uses 1600). Corrected now. */
  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1600px] mx-auto">
      <header className="flex items-start gap-4 mb-5">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0">Agent Discovery</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            {kpis.total} discovered · {kpis.newCount} new · {kpis.investigating} investigating · {filtered.length} match filters
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {/* DEF-DISC-TOOLTIP-SYSLET-001 — Plain-language tooltip.
           * Previously read "Open the Discovery Scan SysLet in Workflow
           * Canvas…" which mixed an internal architecture term (SysLet =
           * platform-internal scheduler agent) with operator-facing copy.
           * Operators saw "SysLet" and wondered if they were in the wrong
           * place — the user explicitly flagged this. The agent here is
           * still architecturally a SysLet (well-known UUID
           * 00000000-0000-0000-0000-000000000402 in
           * SystemIds.DISCOVERY_SCAN_WORKFLOW_ID), but the tooltip just
           * names what the button DOES, not the internal type. */}
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<CalendarClock size={13} />}
            onClick={() => {
              /* P7J-RUN-012 — operators reported clicking this dumped them
               * onto an empty Workflow Canvas with no context. Two failure
               * modes: (a) the canvas honors ?workflowId= but the discovery
               * SysLet seed isn't in the DB on this deployment, or (b) the
               * canvas doesn't pick up the query param. Both leave the user
               * confused. Fire an explanatory toast BEFORE navigating so
               * the destination makes sense even if the workflow doesn't
               * auto-load. */
              addToast({
                tone: 'info',
                title: 'Opening discovery scan scheduler',
                description: 'Editing cron / interval in the trigger node. If the canvas opens empty, the discovery workflow has not been seeded for this tenant — use "+ New workflow" in the canvas to create one.',
              });
              navigate('/canvas?workflowId=00000000-0000-0000-0000-000000000402');
            }}
            title="Open the discovery scan scheduler in Workflow Canvas — edit cron / interval in the trigger config">
            Configure schedule
          </Button>
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Refresh</Button>
          <Button variant="secondary" size="sm" onClick={cycleDensity} title={`Row density: ${density}`}>
            Density: {density.charAt(0).toUpperCase() + density.slice(1)}
          </Button>
          {/* Bug #86 Phase 1 — button-only gate. Disabled with explicit
           * tooltip when zero rules are enabled platform-wide for this
           * tenant. Operator routes via the banner's Configure rules
           * CTA (slideout) or directly to Settings tab. We chose
           * button-disable over click-then-modal per the approved V2
           * mockup: no modals; the visual affordance lives on the
           * disabled control itself, and the help slideout is the
           * single source of guidance. */}
          <Button
            variant="primary"
            size="sm"
            iconLeft={<Search size={13} />}
            onClick={handleScan}
            disabled={ruleStatus != null && (ruleStatus.totalEnabledRules ?? 0) === 0}
            title={
              ruleStatus != null && (ruleStatus.totalEnabledRules ?? 0) === 0
                ? 'Configure at least one rule first — open the rule-status panel from the banner above'
                : 'Run a discovery scan now'
            }>
            Run scan
          </Button>
        </div>
      </header>

      {/* Bug #86 Phase 1 — Amber gating banner. Renders only when the
        * rule-status fetch completes AND zero rules are enabled. Acts
        * as the entry point to the rule-status slideout (no modal).
        * Hidden once any rule is enabled; auto-rehides on the next
        * reload after operator disables every rule. */}
      {ruleStatus != null && (ruleStatus.totalEnabledRules ?? 0) === 0 && (
        <div className="mb-4 px-4 py-3 rounded-v2-md border border-v2-warning-soft bg-v2-warning-soft flex items-center gap-3">
          <AlertTriangle size={18} className="text-v2-warning-strong flex-shrink-0" />
          <div className="flex-1 text-[12.5px] text-v2-warning-strong leading-relaxed">
            <strong>No discovery rules configured.</strong>{' '}
            Scans need at least one enabled rule per engine to detect anything. Without rules, engines complete in milliseconds with zero findings — this is why earlier scans returned 0 agents.
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setRuleSlideoutOpen(true)}
            className="!text-v2-warning-strong">
            Configure rules
          </Button>
        </div>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-5">
        <MetricCard label="Total"         value={kpis.total} tone="cyan" />
        <MetricCard label="New"            value={kpis.newCount} tone="cyan"
          delta={{ value: 'awaiting triage', direction: 'flat', intent: 'neutral' }} />
        <MetricCard label="Investigating"  value={kpis.investigating} tone="warning" />
        <MetricCard label="Confirmed"      value={kpis.confirmed} tone="danger" />
        <MetricCard label="Enrolled"       value={kpis.enrolled} tone="success" />
      </div>

      {/* Phase A · Engine status strip — seven fingerprint engines, one row.
        * BE returns enabled / lastScanAt / signalCount / agentCount per
        * engine. MARKETPLACE always renders grey with a "phase D" pill
        * regardless of the BE response so the slot stays reserved until
        * Phase D wires the marketplace ingestion engine.
        * See docs/AGENT_DISCOVERY_DESIGN.md §18 for the taxonomy. */}
      <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md px-4 py-3 mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold">
            Discovery engines{engineStatus?.totalEnabled != null ? ` · ${engineStatus.totalEnabled} enabled` : ''}
            {engineStatus?.lastScanOverallAt ? ` · last scan ${fmtRelative(engineStatus.lastScanOverallAt)}` : ''}
          </span>
        </div>
        <div className="grid grid-cols-7 gap-2">
          {ENGINE_OPTIONS.map((opt) => {
            const eng = (engineStatus?.engines || []).find(e => e.engine === opt.key) || {};
            const isMarketplace = opt.key === 'MARKETPLACE';
            const enabled = !!eng.enabled && !isMarketplace;
            const dotColor = enabled ? 'bg-v2-success' : 'bg-v2-border-subtle';
            return (
              <div key={opt.key} className="flex items-center gap-1.5" title={
                isMarketplace
                  ? 'Marketplace engine reserved for Phase D'
                  : enabled
                    ? `${opt.label} engine enabled · ${eng.signalCount || 0} signal(s) last scan · ${eng.agentCount || 0} discovered`
                    : `${opt.label} engine disabled — enable rules in Admin`
              }>
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotColor}`} aria-hidden="true" />
                <span className="text-[11.5px] text-v2-ink-secondary">{opt.label}</span>
                {!isMarketplace && (
                  <span className="text-[11px] text-v2-ink-muted">{eng.signalCount ?? 0}</span>
                )}
                {isMarketplace && (
                  <span className="text-[9.5px] text-v2-ink-muted italic ml-0.5">phase D</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Filter chips */}
      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button key={`${c.groupKey}:${c.optionKey}`}
              onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white">
              {c.label} ×
            </button>
          ))}
          <button onClick={clearFacets} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear all</button>
        </div>
      )}

      {/* Bulk toolbar — Phase A: Promote now uses the real bulk-promote
        * endpoint (creates governed Agent rows). Dismiss requires a reason
        * via the inline prompt before firing. */}
      {selectedIds.size > 0 && (
        <div className="bg-v2-action-soft border border-v2-action rounded-v2-md px-4 py-2.5 mb-4 flex items-center gap-3">
          <span className="text-[12.5px] font-semibold text-v2-action-strong">{selectedIds.size} selected</span>
          <Button variant="primary" size="sm" iconLeft={<Activity size={13} />} onClick={() => handleBulk('INVESTIGATING')}>Investigate</Button>
          <Button variant="primary" size="sm" iconLeft={<CheckCircle size={13} />} onClick={handleBulkPromote}>Promote</Button>
          <Button variant="ghost" size="sm" iconLeft={<X size={13} />} onClick={openBulkDismissPrompt}>Dismiss…</Button>
          <button onClick={clearSelected} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear selection</button>
        </div>
      )}

      {/* Phase A · Bulk Dismiss reason prompt */}
      {bulkDismissPromptOpen && (
        <div className="bg-v2-surface border border-v2-warning rounded-v2-md px-4 py-3 mb-4">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <div className="text-[12.5px] font-semibold text-v2-ink mb-1">
                Dismiss {selectedIds.size} discovered agent(s)?
              </div>
              <div className="text-[11.5px] text-v2-ink-muted mb-2">
                Reason is recorded on every dismissed row's audit trail. Cannot be undone via the UI.
              </div>
              <textarea
                value={bulkDismissReason}
                onChange={(e) => setBulkDismissReason(e.target.value)}
                placeholder="e.g. false positive — internal CI runner, not an AI agent"
                rows={2}
                className="w-full text-[12px] rounded-v2-sm border border-v2-border-subtle bg-v2-page text-v2-ink p-2 focus:outline-none focus:border-v2-action"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Button
                variant="danger"
                size="sm"
                disabled={!bulkDismissReason.trim()}
                onClick={handleBulkDismissConfirm}>
                Confirm dismiss
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setBulkDismissPromptOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail
          groups={facetGroupsWithCounts}
          onToggle={toggleFacet}
          ariaLabel="Discovery filters"
        />

        {filtered.length === 0 && !loading ? (
          <EmptyState
            icon={<Bot size={20} />}
            title={agents.length === 0 ? 'No shadow agents discovered' : 'No agents match filters'}
            description={agents.length === 0
              ? 'Run a discovery scan to find AI agents operating outside the platform.'
              : 'Adjust filters or clear them to see more.'}
            primaryAction={agents.length === 0
              ? <Button variant="primary" iconLeft={<Search />} onClick={handleScan}>Run discovery scan</Button>
              : <Button variant="primary" onClick={clearFacets}>Clear filters</Button>}
          />
        ) : (
          <Table
            ariaLabel="Discovered agents"
            density={density}
            columns={[
              { key: 'select', header: '', render: (r) => (
                <input type="checkbox" checked={selectedIds.has(r.id)}
                  onChange={(e) => { e.stopPropagation(); toggleSelected(r.id); }}
                  onClick={(e) => e.stopPropagation()} aria-label={`Select ${r.name || r.id}`} />
              )},
              { key: 'risk', header: 'Risk', render: (r) => {
                const tier = (r.riskTier || r.risk || 'low').toLowerCase();
                return <Pill tone={RISK_TONE[tier] || 'muted'} size="sm" dot>{tier}</Pill>;
              }},
              { key: 'name', header: 'Agent', sortable: true, render: (r) => (
                <div>
                  <div className="font-semibold text-v2-ink">{r.name || r.host || r.id}</div>
                  <div className="text-[10.5px] text-v2-ink-muted">{r.type || r.kind || '—'}{r.host && ` · ${r.host}`}</div>
                </div>
              )},
              { key: 'status', header: 'Status', render: (r) => (
                <Pill tone={STATUS_TONE[r.status] || 'muted'} size="sm" dot>{r.status || 'unknown'}</Pill>
              )},
              { key: 'confidence', header: 'Confidence', render: (r) => {
                const c = typeof r.confidence === 'number' ? r.confidence : parseFloat(r.confidence) || 0;
                const pct = c <= 1 ? Math.round(c * 100) : Math.round(c);
                return <span className="font-mono-v2 text-[12px] tabular-nums">{pct}%</span>;
              }},
              { key: 'engine', header: 'Source', render: (r) => (
                <span className="text-[11.5px] text-v2-ink-secondary">{r.sourceEngine || r.source || r.engine || '—'}</span>
              )},
              { key: 'lastSeen', header: 'Last seen', align: 'right', render: (r) => (
                <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{fmtRelative(r.lastSeen || r.firstSeen)}</span>
              )},
              /* Phase A · sign-off #2 — per-row inline: Investigate + Promote.
                * Dismiss lives behind the overflow menu (⋮) to require a
                * deliberate extra gesture. Enrolled rows show a link to
                * the linked governed Agent instead of triage buttons.
                * For Enrolled rows: no Investigate/Promote, just the trace. */
              { key: 'actions', header: '', align: 'right', render: (r) => {
                if (r.status === 'ENROLLED') {
                  return (
                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      <span className="text-[11px] font-mono-v2 text-v2-ink-muted">
                        {r.enrolledAgentId ? `→ Agent ${String(r.enrolledAgentId).slice(0, 8)}` : 'enrolled'}
                      </span>
                    </div>
                  );
                }
                const rowCan = agentActions(r.status);
                return (
                  <div className="flex items-center justify-end gap-1 relative" onClick={(e) => e.stopPropagation()}>
                    {rowCan.canInvestigate && (
                      <Button variant="ghost" size="sm" onClick={() => handleInvestigate(r.id)} title="Investigate">
                        <Activity size={13} />
                      </Button>
                    )}
                    {rowCan.canPromote && (
                      <Button variant="ghost" size="sm" className="!text-v2-success hover:!bg-v2-success-soft"
                        onClick={() => openPromoteWizard(r)} title="Promote — opens onboarding wizard">
                        <CheckCircle size={13} />
                      </Button>
                    )}
                    {rowCan.canDismiss && (
                      <Button variant="ghost" size="sm" onClick={() => toggleOverflow(r.id)} title="More actions" aria-label="More actions" aria-expanded={overflowOpenId === r.id}>
                        <MoreVertical size={13} />
                      </Button>
                    )}
                    {rowCan.canDismiss && overflowOpenId === r.id && (
                      <div
                        className="absolute right-0 top-full mt-1 z-20 bg-v2-surface border border-v2-border-subtle rounded-v2-sm shadow-v2-md min-w-[140px] py-1"
                        onMouseLeave={() => setOverflowOpenId(null)}>
                        <button
                          onClick={() => { setOverflowOpenId(null); handleDismiss(r.id); }}
                          className="w-full text-left px-3 py-1.5 text-[12px] text-v2-danger hover:bg-v2-danger-soft flex items-center gap-2">
                          <X size={12} aria-hidden="true" /> Dismiss…
                        </button>
                      </div>
                    )}
                  </div>
                );
              }},
            ]}
            rows={visibleRows}
            rowKey={(r) => r.id}
            onRowClick={openSlideout}
          />
        )}
      </div>

      {filtered.length > 0 && (
        <div className="grid gap-5 mt-3 grid-cols-1 lg:grid-cols-[240px_1fr]">
          <div />
          <Pagination
            totalItems={filtered.length}
            page={pg.page}
            pageSize={pg.pageSize}
            onPageChange={pg.setPage}
            onPageSizeChange={pg.setPageSize}
            noun="agents"
          />
        </div>
      )}

      <Slideout
        isOpen={!!selected}
        onClose={closeSlideout}
        ariaLabel={selected ? `${selected.name || selected.id} detail` : 'Agent detail'}
        width={520}
        tabs={[
          { id: 'summary',    label: 'Summary' },
          { id: 'signals',    label: 'Signals' },
          { id: 'graph',      label: 'Communication' },
          { id: 'investigate', label: 'Investigate' },
          { id: 'promote',    label: 'Promote' },
        ]}
        activeTabId={slideoutTab}
        onTabChange={setSlideoutTab}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Pill tone={RISK_TONE[(selected.riskTier || selected.risk || 'low').toLowerCase()] || 'muted'} size="sm" dot>
                {(selected.riskTier || selected.risk || 'low').toLowerCase()} risk
              </Pill>
              <Pill tone={STATUS_TONE[selected.status] || 'muted'} size="sm" dot>{selected.status}</Pill>
            </div>
            <h2 className="text-[18px] font-bold tracking-tight m-0">{selected.name || selected.id}</h2>
            <p className="text-[12px] text-v2-ink-secondary">{selected.type || '—'}{selected.host && ` · ${selected.host}`}</p>
            <div className="flex items-center gap-2 mt-3">
              {selectedCan.canInvestigate && (
                <Button variant="primary" size="sm" iconLeft={<Activity size={13} />}
                  onClick={() => handleInvestigate(selected.id)}>Investigate</Button>
              )}
              {selectedCan.canPromote && (
                <Button variant="primary" size="sm" iconLeft={<CheckCircle size={13} />}
                  onClick={() => openPromoteWizard(selected)}>Promote</Button>
              )}
              {selectedCan.canDismiss && (
                <Button variant="ghost" size="sm" iconLeft={<X size={13} />}
                  onClick={() => handleDismiss(selected.id)}>Dismiss</Button>
              )}
              {selectedCan.canRestore && (
                <Button variant="secondary" size="sm"
                  onClick={() => handleRestore(selected.id)}>Restore to triage</Button>
              )}
              {selected.status === 'ENROLLED' && selected.enrolledAgentId && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(`/agents/${selected.enrolledAgentId}`)}>
                  View protected Agent →
                </Button>
              )}
            </div>
          </div>
        )}
      >
        {selected && slideoutTab === 'summary' && (
          <Card padding="sm">
            <Card.Header title="Detection summary" />
            <Card.Body>
              <dl className="grid grid-cols-2 gap-3 text-[12.5px]">
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">First seen</dt><dd className="text-v2-ink mt-0.5">{selected.firstSeen ? new Date(selected.firstSeen).toLocaleString() : '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Last seen</dt><dd className="text-v2-ink mt-0.5">{selected.lastSeen ? new Date(selected.lastSeen).toLocaleString() : '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Source engine</dt><dd className="text-v2-ink mt-0.5">{selected.sourceEngine || selected.source || '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Confidence</dt><dd className="text-v2-ink mt-0.5">{(() => { const c = typeof selected.confidence === 'number' ? selected.confidence : parseFloat(selected.confidence) || 0; return `${c <= 1 ? Math.round(c * 100) : Math.round(c)}%`; })()}</dd></div>
                {selected.host && <div className="col-span-2"><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Host</dt><dd className="text-v2-ink mt-0.5 font-mono-v2">{selected.host}</dd></div>}
              </dl>
            </Card.Body>
          </Card>
        )}
        {/* A starting trust score for an agent with no runtime evidence behind it yet. Scored from
            what discovery observed, so it is available here the moment an agent is found — before
            anyone decides whether to enrol it. */}
        {selected && slideoutTab === 'summary' && (
          <div style={{ marginTop: 12 }}>
            <TrustScorePanel discoveredAgentId={selected.id} />
          </div>
        )}
        {selected && slideoutTab === 'signals' && (
          <Card padding="sm">
            <Card.Header title="Detection signals" />
            <Card.Body>
              {(selected.signals || signals.filter(s => s.agentId === selected.id) || []).length === 0 ? (
                <p className="text-v2-ink-muted text-[12.5px]">No signals recorded.</p>
              ) : (
                <ul className="space-y-2 text-[12.5px]">
                  {(selected.signals || signals.filter(s => s.agentId === selected.id) || []).map((s, i) => (
                    <li key={s.id || i} className="flex items-baseline gap-2">
                      <span className="font-mono-v2 text-[10.5px] text-v2-ink-muted w-32 flex-shrink-0">{fmtRelative(s.timestamp)}</span>
                      <span><strong className="text-v2-ink">{s.engine || 'system'}</strong>{s.message && <span className="text-v2-ink-secondary"> · {s.message}</span>}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'graph' && (
          <Card padding="sm">
            {/* G367 — Was a /shadow-discovery?legacy=1 bounce. Topology
                graph rendering not yet ported; surface a "coming next
                iteration" hint instead of redirecting to V1. */}
            <Card.Header title="Communication graph" />
            <Card.Body>
              <p className="text-v2-ink-muted text-[12.5px]">
                Communication topology visualization is queued for a follow-up
                iteration. The connection list below shows the same edges in
                tabular form.
              </p>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'investigate' && (
          <Card padding="sm">
            <Card.Header title="Investigation actions" />
            <Card.Body>
              {selectedCan.canInvestigate ? (
                <>
                  <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed mb-3">
                    Mark this agent as Investigating to start a workflow that gathers
                    additional signals from connected engines and quarantines the agent's
                    outbound traffic until a decision is made.
                  </p>
                  <Button variant="primary" size="sm" iconLeft={<Activity size={13} />}
                    onClick={() => handleInvestigate(selected.id)}>
                    Mark as Investigating
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed mb-3">
                    {selectedCan.reason
                      || 'This agent is not awaiting triage, so there is nothing to investigate.'}
                  </p>
                  {selectedCan.canRestore && (
                    <Button variant="secondary" size="sm"
                      onClick={() => handleRestore(selected.id)}>Restore to triage</Button>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'promote' && (
          <Card padding="sm">
            <Card.Header title="Promote to ExteLet" />
            <Card.Body>
              {!selectedCan.canPromote ? (
                <>
                  <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed mb-3">
                    {selectedCan.reason}{selected.status === 'ENROLLED' ? " There's nothing to enroll." : ''}
                  </p>
                  {selected.status === 'ENROLLED' && selected.enrolledAgentId && (
                    <Button variant="secondary" size="sm"
                      onClick={() => navigate(`/agents/${selected.enrolledAgentId}`)}>
                      View protected Agent →
                    </Button>
                  )}
                  {selectedCan.canRestore && (
                    <Button variant="secondary" size="sm"
                      onClick={() => handleRestore(selected.id)}>Restore to triage</Button>
                  )}
                </>
              ) : (
                <>
                  <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed mb-3">
                    Enrolling promotes this discovered agent into a protected ExteLet
                    with policy bindings, trust scoring, and audit trail enabled.
                  </p>
                  <Button variant="primary" size="sm" iconLeft={<CheckCircle size={13} />}
                    onClick={() => handleEnroll(selected.id)}>
                    Enroll as ExteLet
                  </Button>
                </>
              )}
            </Card.Body>
          </Card>
        )}
      </Slideout>

      {/* EXTELET-V3 · Phase 6 — single-row Promote opens the streamlined
       * 6-step onboarding wizard. Bulk Promote elsewhere on the page
       * continues to use the V1 endpoint (unchanged) — see the comment
       * on openPromoteWizard above for why. */}
      <ExteletRegistrationWizard
        isOpen={wizardOpen}
        onClose={closePromoteWizard}
        seedFromDiscovery={wizardSeed}
        onComplete={(envelope) => {
          /* Wizard's "Take me to the Registration tab" button fires this.
           * We close the wizard, refresh the list (so the just-promoted
           * row drops out / changes status), and deep-link to the new
           * agent's Registration tab. */
          closePromoteWizard();
          reload?.();
          const agentId = envelope?.registration?.agentId;
          if (agentId) {
            navigate(`/agents/${agentId}?tab=registration`);
          }
        }}
      />

      {/* Bug #86 Phase 1 — Rule-status Slideout. Opens from the banner
        * (the only entry point — disabled scan button does not pop
        * anything per the approved V2 mockup). Body lists every engine
        * with the canonical "N of M rules" count and a per-engine
        * Configure deep-link to the Settings tab. Phase 4 will replace
        * the Settings deep-link with a one-click "Install preset"
        * action; for now the info tile at the bottom signposts that
        * forward path. */}
      <Slideout
        isOpen={ruleSlideoutOpen}
        onClose={() => setRuleSlideoutOpen(false)}
        ariaLabel="Configure discovery rules"
        width={380}
        header={
          <div>
            <div className="flex items-start gap-2.5">
              <AlertTriangle size={20} className="text-v2-warning-strong mt-0.5" />
              <div className="flex-1">
                <h2 className="text-[15px] font-bold tracking-tight m-0">Configure discovery rules</h2>
                <p className="text-[12px] text-v2-ink-secondary m-0 mt-0.5 leading-relaxed">
                  Every engine is rule-driven. Add at least one rule per engine you want active. Scans refuse to run until at least one rule is enabled platform-wide.
                </p>
              </div>
            </div>
          </div>
        }
      >
        <div className="space-y-3">
          <div className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold">
            Engines · {(ruleStatus?.engines || []).filter(e => (e.enabledCount ?? 0) > 0).length} of {(ruleStatus?.engines || []).length} ready
          </div>

          <div className="space-y-1.5">
            {(ruleStatus?.engines || []).map((eng) => {
              const enabled = eng.enabledCount ?? 0;
              const total = eng.totalCount ?? (eng.supportedRules?.length || 0);
              const ready = enabled > 0;
              return (
                <div
                  key={eng.engineType}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-v2-md bg-v2-muted">
                  <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${ready ? 'bg-v2-success' : 'bg-v2-border'}`} aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold text-v2-ink">{eng.label}</div>
                    <div className="text-[11px] text-v2-ink-muted">
                      {enabled} of {total} rules
                      {eng.supportedRules?.length > 0 && (
                        <> · {eng.supportedRules.slice(0, 3).map(r => r.name).join(', ')}{eng.supportedRules.length > 3 ? ', …' : ''}</>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      /* Phase 1 — deep-link to Settings tab with engine
                       * filter applied. Phase 4 will swap this for a
                       * one-click preset install. */
                      setRuleSlideoutOpen(false);
                      navigate(`/discovery?tab=settings&engine=${encodeURIComponent(eng.engineType)}`);
                    }}>
                    {ready ? 'Manage' : 'Configure'}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Phase 4 (task #95) — Starter pack install CTA. Replaces the
              earlier placeholder note. Clicking calls
              {@code POST /api/v1/discovery/preset/install} which is
              idempotent — re-clicks just report "all 19 already installed".
              On success we close the slideout and reload the page-level
              ruleStatus so the banner + Run-scan button update. */}
          <StarterPackInstallTile
            starterPackSize={ruleStatus?.starterPackSize || 19}
            disabled={starterInstalling}
            installing={starterInstalling}
            onInstall={async () => {
              if (starterInstalling) return;
              setStarterInstalling(true);
              try {
                const r = await api.shadowDiscoveryInstallStarterPack();
                if (r && r.ok === false) throw new Error(r.error || 'Install failed');
                const data = r?.data || r;
                addToast({
                  tone: 'success',
                  title: 'Starter pack installed',
                  description: `${data?.installed ?? 0} new rules · ${data?.skipped ?? 0} skipped (already present). Run scan to use them.`,
                });
                setRuleSlideoutOpen(false);
                reload?.();
              } catch (e) {
                addToast({
                  tone: 'danger',
                  title: 'Starter pack install failed',
                  description: e?.message || String(e),
                });
              } finally {
                setStarterInstalling(false);
              }
            }}
          />
        </div>
      </Slideout>

      {/* Phase 2 picker (regression fix) — single source of truth for
          Run-scan on this page. Both the Agents-tab header button and
          the empty-state CTA open this modal via handleScan; the modal
          calls handlePickerConfirm with an enginePicks payload only
          when the operator clicks "Run N engines · rules" inside it.
          No scan can fire from this page without going through here. */}
      <ScanTriggerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={handlePickerConfirm}
        triggering={!!scanCtx?.triggering}
      />
    </div>
  );
}

/**
 * Phase 4 (task #95) — Starter pack install tile.
 *
 * <p>Lives at the bottom of the rule-status slideout. Replaces the
 * Phase 1 placeholder note ("Phase 4 will add a starter pack…") with
 * the actual one-click install. Renders three states:
 * <ul>
 *   <li>idle — "Install starter pack" + N-rules subtitle</li>
 *   <li>installing — spinner + "Installing…" copy</li>
 *   <li>(no explicit "all installed" state — the success toast +
 *       slideout-close handle that; on re-open with all rules present
 *       the operator can still click and see "0 new · N skipped")</li>
 * </ul>
 *
 * <p>Idempotency guarantee: the BE never replaces or re-enables
 * existing rules. Re-clicking is safe.
 */
function StarterPackInstallTile({ starterPackSize, disabled, installing, onInstall }) {
  return (
    <div className="mt-3 px-3 py-2.5 rounded-v2-md bg-v2-action-soft border border-v2-action/20 text-v2-action-strong">
      <div className="flex items-start gap-2.5">
        <CheckCircle size={16} className="mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-semibold">Install starter pack</div>
          <div className="text-[11.5px] mt-0.5 leading-relaxed text-v2-action">
            {starterPackSize} curated rules across all 6 engines — DNS watchlist of known AI providers, MCP/A2A endpoint detectors, cloud-platform agent probes, identity forensics, behavioural fingerprints. Code-repo rule is installed disabled (needs your GitHub token).
          </div>
          <div className="text-[11px] text-v2-ink-muted mt-1 leading-snug">
            Idempotent — re-clicks skip any rules you already have.
          </div>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={onInstall}
          disabled={disabled}
          loading={installing}
        >
          {installing ? 'Installing…' : 'Install'}
        </Button>
      </div>
    </div>
  );
}

/* ── Resolver ─────────────────────────────────────────────────────── */
export default function ResolvedShadowDiscoveryPage(props) {
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const legacy = url?.searchParams?.get('legacy') === '1';
  if (legacy || !isRebirthDiscoveryEnabled()) return <ShadowDiscoveryPage {...props} />;
  return <ShadowDiscoveryPageV2Inner {...props} />;
}

export { ShadowDiscoveryPageV2Inner };
